import type { AuthCallbackFailure } from "$root/functional-areas/user/oidc";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { Column, Text } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useTranslation } from "react-i18next";

// One message per failure class — the whole point of classifying them. A user who can only
// report what the screen said still tells us which of the three defects they hit.
const messageKeysByFailure = {
	"not-provisioned": { title: "authCallback.NOT_PROVISIONED_TITLE", body: "authCallback.NOT_PROVISIONED_BODY" },
	"sso-protocol": { title: "authCallback.SSO_PROTOCOL_TITLE", body: "authCallback.SSO_PROTOCOL_BODY" },
	network: { title: "authCallback.NETWORK_TITLE", body: "authCallback.NETWORK_BODY" },
	unknown: { title: "authCallback.UNKNOWN_TITLE", body: "authCallback.UNKNOWN_BODY" },
} as const satisfies Record<AuthCallbackFailure, { title: string; body: string }>;

export type AuthCallbackErrorProps = {
	failure: AuthCallbackFailure;
	/**
	 * The only way out — in the app, a real sign-out. It is a prop rather than a direct
	 * `logout()` call so this stays a presentational component: the sign-in screen is a
	 * navigation, and nothing here should be able to trigger one on its own.
	 */
	onBackToSignIn: () => Promise<void>;
};

/**
 * The message a failed auth callback leaves on screen. Full-screen and chromeless like the
 * loader it replaces — the callback route renders outside the app shell, and at this point
 * there is no session to build a shell around anyway.
 */
export function AuthCallbackError({ failure, onBackToSignIn }: AuthCallbackErrorProps): React.JSX.Element {
	const { t } = useTranslation();
	const messageKeys = messageKeysByFailure[failure];

	return (
		<Column alignItems="center" justifyContent="center" style={{ minHeight: "100dvh" }} className="px-4">
			<Column flexGrow={0} gap={16} alignItems="center" className="max-w-md text-center">
				<Text as="h1" type="Title/L/Light">
					{t(messageKeys.title)}
				</Text>
				<Text as="p" type="Body/L/Book" color={themeCSSVars.palette_N500}>
					{t(messageKeys.body)}
				</Text>
				<Button
					palette="primary"
					onClickAsync={() => onBackToSignIn()}
					data-qualifier="authCallback-backToSignIn"
					className="mt-2"
				>
					{t("authCallback.BACK_TO_SIGN_IN")}
				</Button>
			</Column>
		</Column>
	);
}

export default AuthCallbackError;
